'use client';

/**
 * FreestyleDashboard — home view for users on the freestyle (no program) path.
 *
 * Avionics redesign: classification-banner panels, mono typography,
 * tactical accent treatments. Keeps the same data inputs + Link destinations.
 */

import Link from 'next/link';
import { Play, AlertCircle, Dumbbell, Clock, ChevronRight } from 'lucide-react';
import { BarbellIcon } from '@/components/icons/BarbellIcon';
import ProfileCard from '@/components/ProfileCard';
import OpsPanel from '@/components/metrics-ops/OpsPanel';
import FreeLiftWeekTracker from '@/components/dashboard/FreeLiftWeekTracker';
import type { DashboardData } from '@/lib/hooks/useDashboardData';

interface FreestyleDashboardProps {
  data: DashboardData;
}

export default function FreestyleDashboard({ data }: FreestyleDashboardProps) {
  const {
    intelligenceReport,
    activeSession,
    freestyleTemplates,
    recentFreestyleSessions,
  } = data;

  return (
    <div className="space-y-3">
      {/* Profile Card */}
      <ProfileCard compact={true} showEditLink={true} streak={intelligenceReport?.currentState?.weeklySessionCount} />

      {/* Start Workout Hero — full-bleed CTA with corner brackets */}
      <section className="relative overflow-hidden rounded-xl border border-amber-500/40 bg-hud-ink/85">
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/80 to-transparent" />
        <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-amber-500/60" />
        <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/60" />
        <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-amber-500/60" />
        <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/60" />

        <div className="px-4 pt-3 pb-4 flex flex-col items-center text-center">
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-amber-500/90 mb-2">
            Freestyle · Standby
          </div>
          <Dumbbell className="w-9 h-9 text-amber-400 mb-3" aria-hidden style={{ filter: 'drop-shadow(0 0 10px rgba(245,158,11,0.5))' }} />
          <Link
            href="/session/freestyle"
            className="px-8 py-3 bg-amber-500 text-hud-ink font-mono font-bold text-sm uppercase tracking-[0.22em] rounded-md hover:bg-amber-400 transition shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
          >
            Start Workout
          </Link>
        </div>
      </section>

      {/* Week tracker — the no-program "just train" summary */}
      <FreeLiftWeekTracker sessions={recentFreestyleSessions} />

      {/* Active Session Resume */}
      {activeSession && activeSession.type === 'freestyle' && (
        <section className="relative overflow-hidden rounded-xl border border-amber-500/50 bg-hud-ink/85">
          <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500 animate-hud-blink" />
          <div className="pl-4 pr-3 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" aria-hidden />
              <div className="min-w-0">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-amber-400">In Progress</div>
                <div className="font-mono text-[13px] font-bold text-white truncate">Workout active</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                  {activeSession.exerciseCount} exercise{activeSession.exerciseCount !== 1 ? 's' : ''}
                  {activeSession.duration ? ` · ${Math.floor(activeSession.duration / 60)}m elapsed` : ''}
                </div>
              </div>
            </div>
            <Link
              href="/session/freestyle"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-hud-ink font-mono text-[11px] font-bold uppercase tracking-[0.18em] rounded-md hover:bg-amber-400 transition whitespace-nowrap"
            >
              <Play className="w-3.5 h-3.5" />
              Resume
            </Link>
          </div>
        </section>
      )}

      {/* Saved Templates */}
      {freestyleTemplates.length > 0 && (
        <OpsPanel
          badge="Saved Templates"
          callout={
            <Link href="/templates" className="text-amber-400 hover:underline inline-flex items-center gap-1">
              Browse <ChevronRight className="w-3 h-3" />
            </Link>
          }
        >
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {freestyleTemplates.slice(0, 6).map(tmpl => (
              <Link
                key={tmpl.id}
                href={`/session/freestyle?template=${tmpl.id}`}
                className="flex-shrink-0 w-32 p-3 rounded border border-slate-800/80 bg-slate-950/60 hover:border-amber-500/40 hover:bg-amber-500/[0.04] transition"
              >
                <div className="font-mono text-[12px] font-bold text-white truncate">{tmpl.name}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-1">
                  {tmpl.exercises.length} exercise{tmpl.exercises.length !== 1 ? 's' : ''}
                </div>
              </Link>
            ))}
          </div>
        </OpsPanel>
      )}

      {/* Recent Sessions */}
      {recentFreestyleSessions.length > 0 && (
        <OpsPanel
          badge="Recent Sessions"
          callout={
            <Link href="/history" className="text-hud-cyan hover:underline">
              View All
            </Link>
          }
        >
          <ul className="space-y-1.5">
            {recentFreestyleSessions.slice(0, 5).map((session: any) => (
              <li
                key={session.id}
                className="px-3 py-2 rounded border border-slate-800 bg-slate-950/60"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-slate-500 flex-shrink-0" aria-hidden />
                  <div className="min-w-0">
                    <div className="font-mono text-[12px] text-white">Freestyle</div>
                    <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                      {new Date(session.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {session.duration ? ` · ${Math.round(session.duration / 60)}min` : ''}
                      {session.exercises?.length ? ` · ${session.exercises.length} exercises` : ''}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </OpsPanel>
      )}

      {/* Try a Program CTA */}
      <Link
        href="/programs/change"
        className="block relative overflow-hidden rounded-xl border border-amber-500/30 bg-hud-ink/85 hover:border-amber-500/60 hover:bg-amber-500/[0.04] transition"
      >
        <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500" />
        <div className="pl-4 pr-3 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BarbellIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-amber-400">Recommended</div>
              <div className="font-mono text-[13px] font-bold text-white">Want structured training?</div>
              <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                Try a program with weekly progression.
              </div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0" />
        </div>
      </Link>
    </div>
  );
}
